import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCartStore } from '@/features/Cart/stores/cart';
import { cartActions } from '@/features/Cart/services/cartService';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import { getCartStockAdjustments } from '@/features/Cart/services/cartStockAdjustments';
import type { CartItemQuantity } from '@/features/Cart/types/models';
import type { ShopifyCart } from '@/features/Cart/types/servicesShopifyCart';

export const useCartStockValidation = () => {
  const cartStore = useCartStore();
  const { isFreeGiftItem } = cartStore;
  const { cartData, cartDataLoaded, cartUpdateInProgress, latestAddLineIdentities } =
    storeToRefs(cartStore);
  const productStockStore = useProductStockStore();
  const { refreshStock, getExactStock } = productStockStore;
  const { variantStock, unavailableLines } = storeToRefs(productStockStore);
  let isAdjusting = false;
  let hasPendingCheck = false;
  let attemptedSignature: string | null = null;

  const getStockAdjustments = (cart: ShopifyCart): CartItemQuantity[] =>
    getCartStockAdjustments(cart, {
      unavailableLineKeys: unavailableLines.value,
      protectedLineIdentities: latestAddLineIdentities.value,
      isFreeGiftItem,
      getExactStock,
    });

  const stockAdjustments = computed(() => getStockAdjustments(cartData.value));

  const getSignature = () =>
    JSON.stringify([
      cartData.value.items.map(({ key, quantity }) => [key, quantity]),
      variantStock.value,
      unavailableLines.value,
    ]);

  const applyStockAdjustments = async () => {
    if (isAdjusting) {
      hasPendingCheck = true;
      return;
    }
    if (!cartDataLoaded.value || cartUpdateInProgress.value) return;
    if (!stockAdjustments.value.length || getSignature() === attemptedSignature) return;
    isAdjusting = true;
    try {
      await refreshStock({ force: true });
      if (!stockAdjustments.value.length) return;
      attemptedSignature = getSignature();
      await cartActions().updateCartItemQuantities(getStockAdjustments, {
        isStockCorrection: true,
      });
    } finally {
      isAdjusting = false;
      if (hasPendingCheck) {
        hasPendingCheck = false;
        applyStockAdjustments();
      }
    }
  };

  watch([stockAdjustments, cartUpdateInProgress], applyStockAdjustments);
};
